'use client';

import { useMemo, useState } from 'react';
import {
  Shield,
  Eye,
  Target,
  Zap,
  TrendingUp,
  Lightbulb,
  MessageSquare,
  Award,
  ChevronDown,
  ChevronUp,
  AlertCircle,
  AlertTriangle,
  Info,
  CheckCircle,
  Smartphone,
  Sparkles,
  Compass,
} from 'lucide-react';
import type { AuditFinding, BoundingBox } from '@/types/audit';
import { AUDIT_CATEGORIES } from '@/data/audit-criteria';
import { CriterionTag } from './CriterionTag';
import styles from './CategorySection.module.css';

interface CategorySectionProps {
  /** Display label. For a native category this is its localized label. */
  category: string;
  /**
   * Native Audit Criteria category id (FND, VIS, MOB, ...) when the section is
   * one of the twelve; undefined for the keyword fallback buckets. Drives the
   * icon and the description from AUDIT_CATEGORIES so this section, the tag on
   * each card and the PDF describe a finding the same way.
   */
  categoryId?: string;
  findings: AuditFinding[];
  screenshotUrl?: string;
  locale?: string;
  onFindingClick?: (finding: AuditFinding) => void;
  defaultExpanded?: boolean;
}

// Category icons mapping
const CATEGORY_ICONS: Record<string, React.ReactNode> = {
  'WCAG Accessibility': <Shield size={20} />,
  'Visual Hierarchy': <Eye size={20} />,
  'Navigation': <Target size={20} />,
  'Forms & Input': <Zap size={20} />,
  'Mobile Usability': <TrendingUp size={20} />,
  'Performance': <TrendingUp size={20} />,
  'Content & Copy': <MessageSquare size={20} />,
  'Trust & Credibility': <Award size={20} />,
};

// Category descriptions for context
const CATEGORY_DESCRIPTIONS: Record<string, { en: string; es: string }> = {
  'WCAG Accessibility': {
    en: 'Web Content Accessibility Guidelines compliance issues that may prevent users with disabilities from accessing content.',
    es: 'Problemas de cumplimiento de las Pautas de Accesibilidad que pueden impedir a usuarios con discapacidades acceder al contenido.',
  },
  'Visual Hierarchy': {
    en: 'Issues with visual organization that affect how users scan and understand page content.',
    es: 'Problemas con la organización visual que afectan cómo los usuarios escanean y comprenden el contenido.',
  },
  'Navigation': {
    en: 'Navigation structure issues that make it difficult for users to find and access content.',
    es: 'Problemas de estructura de navegación que dificultan a los usuarios encontrar y acceder al contenido.',
  },
  'Forms & Input': {
    en: 'Form design issues that create friction and increase abandonment rates.',
    es: 'Problemas de diseño de formularios que crean fricción y aumentan las tasas de abandono.',
  },
  'Mobile Usability': {
    en: 'Touch target and responsive design issues affecting mobile user experience.',
    es: 'Problemas de objetivos táctiles y diseño responsivo que afectan la experiencia móvil.',
  },
  'Performance': {
    en: 'Loading and rendering issues that impact page speed and user patience.',
    es: 'Problemas de carga y renderizado que impactan la velocidad y la paciencia del usuario.',
  },
  'Content & Copy': {
    en: 'Content clarity and readability issues that affect user comprehension.',
    es: 'Problemas de claridad y legibilidad del contenido que afectan la comprensión del usuario.',
  },
  'Trust & Credibility': {
    en: 'Design elements that may reduce user trust or perceived credibility.',
    es: 'Elementos de diseño que pueden reducir la confianza o credibilidad percibida.',
  },
};

function getSeverityIcon(severity: string, size = 14) {
  switch (severity) {
    case 'critical':
      return <AlertCircle size={size} />;
    case 'high':
      return <AlertTriangle size={size} />;
    case 'medium':
      return <Info size={size} />;
    default:
      return <CheckCircle size={size} />;
  }
}

function getSeverityOrder(severity: string): number {
  const order: Record<string, number> = {
    critical: 0,
    high: 1,
    medium: 2,
    low: 3,
  };
  return order[severity] ?? 2;
}

/**
 * Calculate the combined bounding box that encompasses all findings with bbox data
 */
function getCombinedBbox(findings: AuditFinding[]): BoundingBox | null {
  const boxes = findings
    .filter((f) => f.evidence?.bbox)
    .map((f) => f.evidence!.bbox!);

  if (boxes.length === 0) return null;

  const minX = Math.min(...boxes.map((b) => b.x));
  const minY = Math.min(...boxes.map((b) => b.y));
  const maxX = Math.max(...boxes.map((b) => b.x + b.width));
  const maxY = Math.max(...boxes.map((b) => b.y + b.height));

  // Add padding around the combined area
  const padding = 50;

  return {
    x: Math.max(0, minX - padding),
    y: Math.max(0, minY - padding),
    width: maxX - minX + padding * 2,
    height: maxY - minY + padding * 2,
  };
}

/**
 * CategorySection Component
 * Displays findings grouped by category with optional screenshot crop
 * Follows design system: bordered items pattern, monospace labels
 */
// Icons for the twelve native categories, by id.
const NATIVE_ICONS: Record<string, React.ReactNode> = {
  FND: <Lightbulb size={20} />,
  STD: <CheckCircle size={20} />,
  VIS: <Eye size={20} />,
  CONT: <MessageSquare size={20} />,
  NAV: <Compass size={20} />,
  INT: <Zap size={20} />,
  FB: <AlertCircle size={20} />,
  ERR: <AlertTriangle size={20} />,
  CTRL: <Target size={20} />,
  MOB: <Smartphone size={20} />,
  ACC: <Shield size={20} />,
  AIX: <Sparkles size={20} />,
};

export function CategorySection({
  category,
  categoryId,
  findings,
  screenshotUrl,
  locale = 'en',
  onFindingClick,
  defaultExpanded = true,
}: CategorySectionProps) {
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);

  // Sort findings by severity
  const sortedFindings = useMemo(() => {
    return [...findings].sort((a, b) => getSeverityOrder(a.severity) - getSeverityOrder(b.severity));
  }, [findings]);

  // Get combined bounding box for screenshot crop
  const combinedBbox = useMemo(() => getCombinedBbox(findings), [findings]);

  // Get severity summary
  const severitySummary = useMemo(() => {
    return findings.reduce(
      (acc, f) => {
        acc[f.severity] = (acc[f.severity] || 0) + 1;
        return acc;
      },
      {} as Record<string, number>
    );
  }, [findings]);

  const lang = (locale === 'es' ? 'es' : 'en') as 'en' | 'es';
  const native = categoryId ? AUDIT_CATEGORIES.find((c) => c.id === categoryId) : undefined;
  const icon = (categoryId && NATIVE_ICONS[categoryId]) || CATEGORY_ICONS[category] || <Lightbulb size={20} />;
  const description = native
    ? native.blurb[lang]
    : CATEGORY_DESCRIPTIONS[category]?.[lang] || CATEGORY_DESCRIPTIONS[category]?.en || '';

  const i18n = {
    en: {
      issues: 'issues',
      viewDetails: 'View Details',
      affectedElements: 'Affected Elements',
      recommendation: 'Recommendation',
    },
    es: {
      issues: 'problemas',
      viewDetails: 'Ver Detalles',
      affectedElements: 'Elementos Afectados',
      recommendation: 'Recomendación',
    },
  };
  const t = i18n[locale as 'en' | 'es'] || i18n.en;

  return (
    <section className={styles.container}>
      {/* Category Header */}
      <button
        className={styles.header}
        onClick={() => setIsExpanded(!isExpanded)}
        aria-expanded={isExpanded}
      >
        <div className={styles.headerLeft}>
          <div className={styles.icon}>{icon}</div>
          <div className={styles.headerText}>
            <span className={styles.categoryLabel}>{category.toUpperCase()}</span>
            <div className={styles.headerStats}>
              <span className={styles.issueCount}>
                {findings.length} {t.issues}
              </span>
              <div className={styles.severityPills}>
                {severitySummary.critical && (
                  <span className={`${styles.severityPill} ${styles.critical}`}>
                    {severitySummary.critical} critical
                  </span>
                )}
                {severitySummary.high && (
                  <span className={`${styles.severityPill} ${styles.high}`}>
                    {severitySummary.high} high
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>
        <div className={styles.expandIcon}>
          {isExpanded ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
        </div>
      </button>

      {/* Expanded Content */}
      {isExpanded && (
        <div className={styles.content}>
          {/* Category Description */}
          {description && <p className={styles.description}>{description}</p>}

          {/* Screenshot Crop (if available) */}
          {screenshotUrl && combinedBbox && (
            <div className={styles.screenshotCrop}>
              <div
                className={styles.screenshotFrame}
                style={{
                  // Use object-position to show the relevant portion
                  '--crop-x': `${combinedBbox.x}px`,
                  '--crop-y': `${combinedBbox.y}px`,
                  '--crop-width': `${combinedBbox.width}px`,
                  '--crop-height': `${Math.min(combinedBbox.height, 400)}px`,
                } as React.CSSProperties}
              >
                <img
                  src={screenshotUrl}
                  alt={`Screenshot showing ${category} issues`}
                  className={styles.screenshotImage}
                />
              </div>
              <span className={styles.screenshotCaption}>
                Affected area on analyzed page
              </span>
            </div>
          )}

          {/* Findings List */}
          <div className={styles.findingsList}>
            {sortedFindings.map((finding, index) => (
              <div
                key={finding.principleId || index}
                className={styles.findingItem}
                onClick={() => onFindingClick?.(finding)}
                role={onFindingClick ? 'button' : undefined}
                tabIndex={onFindingClick ? 0 : undefined}
              >
                {/* Severity Indicator */}
                <div className={`${styles.severityIndicator} ${styles[finding.severity]}`}>
                  {getSeverityIcon(finding.severity)}
                </div>

                {/* Finding Content */}
                <div className={styles.findingContent}>
                  <div className={styles.findingHeader}>
                    <h4 className={styles.findingTitle}>{finding.principleTitle}</h4>
                    <span className={`${styles.severityTag} ${styles[finding.severity]}`}>
                      {finding.severity}
                    </span>
                    <CriterionTag code={finding.auditCode} categoryLabel={finding.auditCategoryLabel} />
                  </div>

                  <p className={styles.findingDescription}>{finding.description}</p>

                  {/* Affected Elements */}
                  {finding.affectedElements && finding.affectedElements.length > 0 && (
                    <div className={styles.affectedElements}>
                      <span className={styles.elementLabel}>{t.affectedElements}:</span>
                      <div className={styles.elementTags}>
                        {finding.affectedElements.slice(0, 3).map((el, i) => (
                          <code key={i} className={styles.elementTag}>{el}</code>
                        ))}
                        {finding.affectedElements.length > 3 && (
                          <span className={styles.moreElements}>
                            +{finding.affectedElements.length - 3} more
                          </span>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Recommendation */}
                  {finding.recommendation && (
                    <div className={styles.recommendation}>
                      <span className={styles.recommendationLabel}>{t.recommendation}:</span>
                      <p className={styles.recommendationText}>{finding.recommendation}</p>
                    </div>
                  )}

                  {/* Evidence Info */}
                  {finding.evidence?.aboveFold !== undefined && (
                    <div className={styles.evidenceTags}>
                      {finding.evidence.aboveFold && (
                        <span className={styles.aboveFoldTag}>Above fold</span>
                      )}
                      {finding.effortEstimate && (
                        <span className={styles.effortTag}>{finding.effortEstimate} fix</span>
                      )}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </section>
  );
}

export default CategorySection;
